import { Card, CardButtonActionMode, HeaderGradient, MiniCard, Row } from '@app/components';
import React, { memo } from 'react';
import { theme } from '@app/theme';
import { useDynamicSizes } from '@app/hooks';
import LinearGradient from 'react-native-linear-gradient';
import { ScrollView, StyleSheet } from 'react-native';
import { IVariation, UNITARY_VARIATION_NAME } from '@app/common';
import { useAppSelector } from '@app/store/hooks';
import { triptychSelectors } from '@app/store';
import SvgInfo from '@app/components/icons/SvgInfo';
import { SlantTitle } from '@app/components/typography';

type VariationCardProps = {
  variation?: Partial<IVariation>;
  toggle: (mode: CardButtonActionMode) => void;
};

const formatCardData = (partialVariation: Partial<IVariation>) => {
  return {
    name: partialVariation?.name || 'Nouvelle variation'
  }
}

export const VariationCard = memo(({ toggle, variation }: VariationCardProps) => {
  const { subContainerHeight } = useDynamicSizes();

  const partialVariation = useAppSelector<Partial<IVariation>>(triptychSelectors.selectData);
  const cardData = useAppSelector<Partial<IVariation>>(triptychSelectors.selectCardData);

  const { name } = formatCardData(cardData ?? partialVariation);

  return (
    <Card>

      <HeaderGradient>
        <LinearGradient
          start={{ x: 0.5, y: 0 }}
          end={{ x: 0.5, y: 1 }}
          locations={[0, 1]}
          colors={['rgba(253, 255, 252, 0)', 'rgba(253, 255, 252, 1)']}
          style={styles.headerGradient}
        />
      </HeaderGradient>

      <Card.TopHeader>
        <Card.TopHeaderLabel>Fiche variation</Card.TopHeaderLabel>
      </Card.TopHeader>

      <Card.Container height={subContainerHeight}>
        <Card.Background source={require('@app/assets/img/cardbackground.png')} />

        <Card.Content>
          <Card.Header>
            {/* En-tête */}
            <Row justify="space-between" align="center">
              <Card.HeaderTitles>
                <Card.Title numberOfLines={2}>{name}</Card.Title>
                {variation?._id && <Card.Subtitle>Ref: {variation._id}</Card.Subtitle>}
              </Card.HeaderTitles>

              {variation?.name !== UNITARY_VARIATION_NAME && (
                <Card.ButtonAction onPress={toggle} permissionScope="variations" />
              )}
            </Row>
          </Card.Header>
        </Card.Content>

        <MiniCard>
          <MiniCard.Tabs>
            <MiniCard.TabsHolder>
              <MiniCard.Tab Icon={SvgInfo} isActive />
            </MiniCard.TabsHolder>
          </MiniCard.Tabs>
          <MiniCard.Container>
            <MiniCard.Content>
              <SlantTitle label="Aide" color={theme.colors.gold} />

              <Card.HelpScrollContainer>

                <Card.LineBreak height={16} />

                <Card.HelpTitle>Fonctionnement des variations</Card.HelpTitle>

                <Card.LineBreak height={8} />

                <Card.HelpText>
                  Certains produits existent en plusieurs formats ou tailles (par exemple : Pizza 30 cm / Pizza 40 cm, ou Coca-Cola 33 cl / 1,5 L).
                </Card.HelpText>

                <Card.HelpText>
                  Pour gérer cela dans Tick’eat, il faut utiliser le système de déclinaisons, basé sur les variations.
                </Card.HelpText>

                <Card.HelpText>Pour suivre le tutoriel complet, rendez-vous <Card.HelpLink href="https://tickeat.featurebase.app/en/help/articles/2548147-creer-des-declinaisons-pour-un-produit">ici</Card.HelpLink></Card.HelpText>

                <Card.LineBreak height={348} />

              </Card.HelpScrollContainer>
            </MiniCard.Content>
          </MiniCard.Container>
        </MiniCard>
      </Card.Container>
    </Card>
  );
});


const styles = StyleSheet.create({
  headerGradient: {
    flex: 1,
    height: theme.sizes.listHeaderHeight,
  },
});
